import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';

function ScoreSkeleton() {
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Skeleton variant="text" width={280} height={48} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={420} height={28} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={360} height={24} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={120} height={42} />
          <Skeleton variant="rounded" width={160} height={42} />
        </Box>
      </Box>

      {/* Macro Summary Table */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="text" width={200} height={32} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Box 
                  key={i}
                  sx={{ 
                    textAlign: 'center', 
                    px: 2, 
                    py: 1, 
                    bgcolor: alpha('#667eea', 0.05), 
                    borderRadius: 1,
                    minWidth: 100
                  }}
                >
                  <Skeleton variant="text" width={60} height={20} sx={{ mx: 'auto', mb: 0.5 }} />
                  <Skeleton variant="text" width={50} height={32} sx={{ mx: 'auto' }} />
                </Box>
              ))}
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((row) => (
                  <TableRow key={row} hover>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={20} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="text" width={90} height={20} sx={{ mx: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={80} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={70} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={70} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="circular" width={16} height={16} sx={{ mx: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="rounded" width={60} height={24} sx={{ ml: 'auto', borderRadius: 4 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="rounded" width={40} height={24} sx={{ mx: 'auto', borderRadius: 4 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={50} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="90%" height={20} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pillar Scores Table */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#764ba2', 0.05) }}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={100} height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                  <TableRow key={row} hover>
                    <TableCell>
                      <Skeleton variant="rounded" width={40} height={28} sx={{ borderRadius: 4 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <Skeleton variant="rounded" width={80} height={6} />
                        <Skeleton variant="text" width={45} height={20} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="rounded" width={90} height={24} sx={{ mx: 'auto', borderRadius: 4 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Indicator Scores Table */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((row) => (
                  <TableRow key={row} hover>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="85%" height={20} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="text" width={90} height={20} sx={{ mx: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={80} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={70} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={70} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="circular" width={16} height={16} sx={{ mx: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="rounded" width={60} height={24} sx={{ ml: 'auto', borderRadius: 4 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="rounded" width={40} height={24} sx={{ mx: 'auto', borderRadius: 4 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={50} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent)',
          animation: 'shimmer 2s infinite',
          zIndex: 9999,
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />
    </Box>
  );
}

export default ScoreSkeleton;

